import React, { useState } from 'react';
import PropTypes from 'prop-types';
import noop from 'lodash/noop';
import { Flyout } from '@els/els-react--flyout';
import AvailabilityProviders from '@Components/Results/structure/availability/AvailabilityProviders';
import { getShoppingCartIcon, loadShoppingCartImage } from '@Components/Results/structure/availability/ShoppingCartHelper';
import Auth from '@Services/Auth';
import { FEATURES } from '@Constants/rx-ui/constants';
import HoverTooltip from '@Components/HoverTooltip';
import { TooltipConstants } from '@Components/Tooltip';
import ReactDOMServer from 'react-dom/server';
import ShoppingCartTooltipContent from '@Components/Results/structure/availability/ShoppingCartTooltipContent';
import Catalyst from '@Services/Catalyst';
import { Badge } from '@els/els-react--badge';
import NumberFormatter from '@Utils/NumberFormatter';

import './ShoppingCartContent.scss';

function ShoppingCartContent({
  tooltipId,
  tooltipPlace,
  supplierNumber,
  structure,
  trackingContext,
  onOptionClick = () => null,
  availability,
  onOpen = () => null,
  trackingContent,
}) {
  const [isOpen, setIsOpen] = useState(false);

  const toggleMenu = (isOpenValue) => {
    const open = typeof isOpenValue === 'boolean' ? isOpenValue : isOpenValue?.isOpen;
    setIsOpen(open);
    if (open) {
      const tooltip = document.getElementById(TooltipConstants.ids.GlobalHover);
      if (tooltip) {
        tooltip.dispatchEvent(
          new MouseEvent('mouseleave', {
            bubbles: true,
          })
        );
      }
      onOpen();
      Catalyst.trackEvent('buttonClick', {
        buttonType: 'menu:shopping cart',
        content: trackingContent,
      });
    }
  };

  const details = structure.details || {};
  const tooltipLabel = 'Commercial Suppliers';
  const shoppingCartTooltipLabel = 'Number of Suppliers';

  return (
    <div className="shopping-cart-block">
      {Auth.isFeatureEnabled(FEATURES.enableDisplaySupplierNumber) && !!supplierNumber && (
        <HoverTooltip
          id={tooltipId}
          tooltip={shoppingCartTooltipLabel}
          className="shopping-cart-tooltip"
          place={tooltipPlace}
          theme="simple">
          <Badge
            type="subtle"
            label={shoppingCartTooltipLabel}
            title={tooltipId ? null : shoppingCartTooltipLabel}
            className="supplier-number-badge"
            content={NumberFormatter(supplierNumber)}
          />
        </HoverTooltip>
      )}

      <Flyout
        className="structure-toolbar-options"
        flyout={
          <div className="u-els-nowrap" data-testid="shopping-cart-flyout">
            <div className="u-els-margin-bottom-1o2 u-els-margin-right-2x u-els-font-size-h4">Substance Availability</div>
            <AvailabilityProviders
              closeMenu={noop}
              providers={availability.providers}
              loadImage={loadShoppingCartImage}
              structure={structure}
              trackingContext={trackingContext}
              onProviderClick={onOptionClick}
              isOpen={isOpen}
            />
          </div>
        }
        shouldCloseOnESC
        showCloseButton
        onToggle={toggleMenu}
        placement="bottom-start"
        pointerType="primary">
        <HoverTooltip
          id={TooltipConstants.ids.GlobalHover}
          tooltip={ReactDOMServer.renderToString(<ShoppingCartTooltipContent details={details} />)}
          place={tooltipPlace}
          theme="simple">
          <button
            type="button"
            onClick={() => toggleMenu(!isOpen)}
            aria-label="Substance Availability"
            title={tooltipId ? null : tooltipLabel}
            className="els-btn els-btn-link els-btn-sm structure-options-button e2e-dropdown e2e-substance-availability structure-availability-button">
            <svg className="els-btn-icon els-btn-icon-sm" focusable="false">
              <use xlinkHref={getShoppingCartIcon(details)} />
            </svg>
            <span className="screen-reader-text">Substance Availability</span>
          </button>
        </HoverTooltip>
      </Flyout>
    </div>
  );
}

ShoppingCartContent.propTypes = {
  availability: PropTypes.object.isRequired,
  structure: PropTypes.object.isRequired,
  trackingContext: PropTypes.string,
  onOpen: PropTypes.func,
  onOptionClick: PropTypes.func,
  supplierNumber: PropTypes.number,
  tooltipId: PropTypes.string,
  tooltipPlace: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  trackingContent: PropTypes.array,
};

export default ShoppingCartContent;
